import { ThemeColors } from '@/src/context/ThemeContext';
import { League, PostWithDetails, Tournament } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { LeagueCard, PatternBackground, PostCard, TournamentCard } from '@/src/presentation/components';
import { LoadingSpinner } from '@/src/presentation/components/LoadingSpinner';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { RefreshControl, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './HomeDashboardScreen.styles';

export interface HomeDashboardScreenProps {
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  userName: string;
  currentUserId: string;
  activeTournaments: Tournament[];
  pendingTournamentInvitationsCount: number;
  pendingTeamInvitationsCount: number;
  pendingFriendRequestsCount: number;
  pendingChallengesCount: number;
  activeLeagues: League[];
  recentPosts: PostWithDetails[];
  publicTournaments: Tournament[];
  onCreateTournament: () => void;
  onCreateLeague: () => void;
  onTournamentPress: (id: string) => void;
  onLeaguePress: (id: string) => void;
  onPostUserPress: (userId: string) => void;
  onPostTournamentPress: (tournamentId: string) => void;
  onSeeAllTournaments: () => void;
  onSeeAllLeagues: () => void;
  onSeeAllFeed: () => void;
  onGoToInvitations: () => void;
  onGoToFriendRequests: () => void;
  onGoToChallenges: () => void;
  onExplore: () => void;
  onPublicTournamentPress: (id: string) => void;
  colors: ThemeColors;
}

export const HomeDashboardScreen = ({
  loading,
  refreshing,
  onRefresh,
  userName,
  currentUserId,
  activeTournaments,
  pendingTournamentInvitationsCount,
  pendingTeamInvitationsCount,
  pendingFriendRequestsCount,
  pendingChallengesCount,
  activeLeagues,
  recentPosts,
  publicTournaments,
  onCreateTournament,
  onCreateLeague,
  onTournamentPress,
  onLeaguePress,
  onPostUserPress,
  onPostTournamentPress,
  onSeeAllTournaments,
  onSeeAllLeagues,
  onSeeAllFeed,
  onGoToInvitations,
  onGoToFriendRequests,
  onGoToChallenges,
  onExplore,
  onPublicTournamentPress,
  colors,
}: HomeDashboardScreenProps) => {
  const { t } = useTranslation();
  const styles = createStyles(colors);

  const hasPendingActions =
    pendingTournamentInvitationsCount > 0 || pendingTeamInvitationsCount > 0 || pendingFriendRequestsCount > 0 || pendingChallengesCount > 0;

  const isNewUser =
    activeTournaments.length === 0 && activeLeagues.length === 0 && recentPosts.length === 0;

  if (loading) {
    return <LoadingSpinner color={colors.primary} backgroundColor={colors.background} />;
  }

  return (
    <PatternBackground>
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.container}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
      showsVerticalScrollIndicator={false}
    >
      {/* Header: greeting + quick actions */}
      <View style={styles.header}>
        <Text style={styles.greeting}>{t('home.welcomeBack', { name: userName })}</Text>
        <View style={styles.quickActions}>
          <TouchableOpacity style={styles.quickActionBtn} onPress={onCreateTournament} activeOpacity={0.8}>
            <Ionicons name="add" size={18} color={colors.primaryText} />
            <Text style={styles.quickActionText}>{t('home.createTournament')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.quickActionBtn, styles.quickActionBtnSecondary]}
            onPress={onCreateLeague}
            activeOpacity={0.8}
          >
            <MaterialCommunityIcons name="trophy-outline" size={18} color={colors.text} />
            <Text style={[styles.quickActionText, styles.quickActionTextSecondary]}>{t('home.createLeague')}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Getting Started */}
      {isNewUser && (
        <View style={styles.section}>
          <View style={styles.gettingStartedCard}>
            <View style={styles.gettingStartedHeader}>
              <Ionicons name="rocket-outline" size={20} color={colors.primary} />
              <Text style={styles.gettingStartedTitle}>{t('home.gettingStartedTitle')}</Text>
            </View>
            <TouchableOpacity style={styles.gettingStartedItem} onPress={onCreateTournament} activeOpacity={0.7}>
              <View style={styles.gettingStartedIconWrap}>
                <Ionicons name="trophy-outline" size={18} color={colors.primary} />
              </View>
              <Text style={styles.gettingStartedItemText}>{t('home.gettingStartedStep1')}</Text>
              <Ionicons name="chevron-forward" size={16} color={colors.textTertiary} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.gettingStartedItem} onPress={onExplore} activeOpacity={0.7}>
              <View style={styles.gettingStartedIconWrap}>
                <Ionicons name="search-outline" size={18} color={colors.primary} />
              </View>
              <Text style={styles.gettingStartedItemText}>{t('home.gettingStartedStep2')}</Text>
              <Ionicons name="chevron-forward" size={16} color={colors.textTertiary} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.gettingStartedItem} onPress={onCreateLeague} activeOpacity={0.7}>
              <View style={styles.gettingStartedIconWrap}>
                <MaterialCommunityIcons name="trophy-outline" size={18} color={colors.primary} />
              </View>
              <Text style={styles.gettingStartedItemText}>{t('home.gettingStartedStep3')}</Text>
              <Ionicons name="chevron-forward" size={16} color={colors.textTertiary} />
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Pending Actions */}
      {hasPendingActions && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>{t('home.pendingActions')}</Text>
          </View>
          <View style={styles.pendingActionsContainer}>
            {pendingTournamentInvitationsCount > 0 && (
              <TouchableOpacity style={styles.pendingActionCard} onPress={onGoToInvitations} activeOpacity={0.8}>
                <View style={styles.pendingActionIconContainer}>
                  <Ionicons name="mail-outline" size={20} color={colors.primary} />
                </View>
                <Text style={styles.pendingActionText}>
                  {t('home.tournamentInvitationsPending', { count: pendingTournamentInvitationsCount })}
                </Text>
                <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} style={styles.pendingActionChevron} />
              </TouchableOpacity>
            )}
            {pendingTeamInvitationsCount > 0 && (
              <TouchableOpacity style={styles.pendingActionCard} onPress={onGoToInvitations} activeOpacity={0.8}>
                <View style={styles.pendingActionIconContainer}>
                  <Ionicons name="people-outline" size={20} color={colors.primary} />
                </View>
                <Text style={styles.pendingActionText}>
                  {t('home.teamInvitationsPending', { count: pendingTeamInvitationsCount })}
                </Text>
                <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} style={styles.pendingActionChevron} />
              </TouchableOpacity>
            )}
            {pendingFriendRequestsCount > 0 && (
              <TouchableOpacity style={styles.pendingActionCard} onPress={onGoToFriendRequests} activeOpacity={0.8}>
                <View style={styles.pendingActionIconContainer}>
                  <Ionicons name="person-add-outline" size={20} color={colors.primary} />
                </View>
                <Text style={styles.pendingActionText}>
                  {t('home.friendRequestsPending', { count: pendingFriendRequestsCount })}
                </Text>
                <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} style={styles.pendingActionChevron} />
              </TouchableOpacity>
            )}
            {pendingChallengesCount > 0 && (
              <TouchableOpacity style={styles.pendingActionCard} onPress={onGoToChallenges} activeOpacity={0.8}>
                <View style={styles.pendingActionIconContainer}>
                  <MaterialCommunityIcons name="sword-cross" size={20} color={colors.primary} />
                </View>
                <Text style={styles.pendingActionText}>
                  {t('home.challengesPending', { count: pendingChallengesCount })}
                </Text>
                <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} style={styles.pendingActionChevron} />
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}

      {/* Active Tournaments */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>{t('home.activeTournaments')}</Text>
          <TouchableOpacity style={styles.seeAllBtn} onPress={onSeeAllTournaments}>
            <Text style={styles.seeAllText}>{t('home.seeAll')}</Text>
          </TouchableOpacity>
        </View>
        {activeTournaments.length === 0 ? (
          <View style={styles.emptyCard}>
            <Ionicons name="trophy-outline" size={28} color={colors.textTertiary} />
            <Text style={styles.emptyCardText}>{t('home.noActiveTournaments')}</Text>
            <TouchableOpacity onPress={onCreateTournament}>
              <Text style={styles.emptyCardAction}>{t('home.createTournament')}</Text>
            </TouchableOpacity>
          </View>
        ) : (
          activeTournaments.map((tournament) => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament}
              onPress={() => onTournamentPress(tournament.id)}
              colors={colors}
            />
          ))
        )}
      </View>

      {/* My Leagues */}
      {activeLeagues.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>{t('home.myLeagues')}</Text>
            <TouchableOpacity style={styles.seeAllBtn} onPress={onSeeAllLeagues}>
              <Text style={styles.seeAllText}>{t('home.seeAll')}</Text>
            </TouchableOpacity>
          </View>
          {activeLeagues.map((league) => (
            <LeagueCard
              key={league.id}
              league={league}
              onPress={() => onLeaguePress(league.id)}
              colors={colors}
            />
          ))}
        </View>
      )}

      {/* Recent Activity */}
      {recentPosts.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>{t('home.recentActivity')}</Text>
            <TouchableOpacity style={styles.seeAllBtn} onPress={onSeeAllFeed}>
              <Text style={styles.seeAllText}>{t('home.seeAll')}</Text>
            </TouchableOpacity>
          </View>
          {recentPosts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              currentUserId={currentUserId}
              onUserPress={onPostUserPress}
              onTournamentPress={onPostTournamentPress}
              colors={colors}
            />
          ))}
        </View>
      )}

      {/* Discover */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>{t('home.discover')}</Text>
          <TouchableOpacity style={styles.seeAllBtn} onPress={onExplore}>
            <Text style={styles.seeAllText}>{t('home.explore')}</Text>
          </TouchableOpacity>
        </View>
        {publicTournaments.length === 0 ? (
          <View style={styles.emptyCard}>
            <Ionicons name="earth-outline" size={28} color={colors.textTertiary} />
            <Text style={styles.emptyCardText}>{t('home.noPublicTournaments')}</Text>
            <TouchableOpacity onPress={onCreateTournament}>
              <Text style={styles.emptyCardAction}>{t('home.createTournament')}</Text>
            </TouchableOpacity>
          </View>
        ) : (
          publicTournaments.map((tournament) => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament}
              onPress={() => onPublicTournamentPress(tournament.id)}
              colors={colors}
            />
          ))
        )}
      </View>

      <View style={styles.bottomPadding} />
    </ScrollView>
    </PatternBackground>
  );
};
